import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ease = [0.76, 0, 0.24, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.3, ease, delay }}>
      {children}
    </motion.span>
  </span>
);

const SLIDES = [
  { src: "/img/hero-mimi.jpg", pos: "50% 0%" },
  { src: "/img/portrait-bw.jpg", pos: "50% 20%" },
  { src: "/img/portrait-hot.jpg", pos: "50% 12%" },
  { src: "/img/portrait-blue.jpg", pos: "50% 18%" },
  { src: "/img/portrait-beauty.jpg", pos: "50% 30%" },
  { src: "/img/portrait-hair.jpg", pos: "40% 35%" },
];

const Slideshow = () => {
  const [[i, prev], setSlide] = useState([0, null]);
  useEffect(() => {
    const t = setInterval(() => setSlide(([v]) => [(v + 1) % SLIDES.length, v]), 5000);
    return () => clearInterval(t);
  }, []);
  return SLIDES.map((s, k) => {
    const state = k === i ? "z-20 opacity-100 hero-kenburns" : k === prev ? "z-10 opacity-100 hero-kenburns" : "z-0 opacity-0";
    return (
      <img key={s.src} src={s.src} alt={k === 0 ? "Mimi Tao portrait" : ""} data-testid={`hero-slide-${k}`}
        style={{ objectPosition: s.pos, transition: k === i ? "opacity 2600ms ease-in-out" : "none" }}
        className={`absolute inset-0 h-full w-full object-cover ${state}`} />
    );
  });
};

const Portrait = ({ y }) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const sx = useSpring(rx, { stiffness: 80, damping: 18 });
  const sy = useSpring(ry, { stiffness: 80, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  return (
    <motion.div style={{ y }} className="relative [perspective:1200px]" onMouseMove={move}
      onMouseLeave={() => { rx.set(0); ry.set(0); }}>
      <motion.div style={{ rotateX: sx, rotateY: sy }}
        initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.6, ease, delay: 0.5 }}
        className="relative aspect-[4/5] w-full overflow-hidden" data-testid="hero-portrait">
        <motion.div initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease, delay: 0.5 }}
          className="absolute inset-0">
          <Slideshow />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const nav = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden pt-28 pb-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_35%,rgba(228,210,191,0.75),transparent_60%)]" />
      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-10 grid desk:grid-cols-12 gap-12 desk:gap-8 items-end">
        <motion.div style={{ y: textY }} className="desk:col-span-7 desk:pb-10 relative z-10 text-center desk:text-left">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] mb-8">
            Actress — Model — Activist
          </motion.p>
          <div className="relative">
            <motion.video data-testid="hero-title-video" src="/video/hero-soft.mp4" poster="/video/hero-soft.jpg"
              autoPlay muted loop playsInline preload="auto" aria-hidden
              initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} transition={{ duration: 2.4, delay: 0.6 }}
              className="pointer-events-none absolute -left-8 -right-8 -top-16 -bottom-12 sm:-left-16 sm:-right-24 h-[calc(100%+7rem)] w-[calc(100%+4rem)] sm:w-[calc(100%+10rem)] object-cover object-[50%_40%] sepia-[0.25] [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_72%)] [-webkit-mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_72%)]" />
            <h1 data-testid="hero-title" className="relative font-display font-normal leading-[0.86] tracking-[-0.03em] text-[min(19vw,190px)] desk:text-[14vw] 2xl:text-[230px] flex justify-center gap-[0.22em] desk:block">
              <Line delay={0.15}>Mimi</Line>
              <Line delay={0.3} className="italic pr-[0.06em] desk:pl-[8vw] text-[var(--gold)]">Tao</Line>
            </h1>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 flex flex-wrap items-center justify-center desk:justify-start gap-4">
            <button data-testid="hero-acting-btn" onClick={() => nav("/acting")}
              className="group inline-flex items-center gap-3 rounded-full bg-[var(--ink)] text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--gold)] transition-colors duration-500">
              View the work <ArrowDownRight size={16} className="transition-transform duration-500 group-hover:rotate-[-45deg]" />
            </button>
          </motion.div>
        </motion.div>
        <div className="desk:col-span-5 desk:col-start-8 w-[85%] sm:w-[65%] sm:max-w-[560px] desk:max-w-none desk:w-full mx-auto desk:mr-0 desk:ml-auto">
          <Portrait y={imgY} />
        </div>
      </div>
    </section>
  );
};
